import type { LanguageCode, RouteInstruction } from '../types';

// Verified GraphHopper sign meanings:
// https://github.com/graphhopper/graphhopper/blob/master/docs/web/api-doc.md#parameters
const actions: Record<LanguageCode, Record<number, string>> = {
  EN: {
    [-7]: 'Keep left',
    [-3]: 'Turn sharp left',
    [-2]: 'Turn left',
    [-1]: 'Turn slight left',
    [0]: 'Continue straight',
    [1]: 'Turn slight right',
    [2]: 'Turn right',
    [3]: 'Turn sharp right',
    [4]: 'You have arrived',
    [5]: 'Via point reached',
    [6]: 'Enter the roundabout',
    [7]: 'Keep right'
  },
  DE: {
    [-7]: 'Links halten',
    [-3]: 'Scharf links abbiegen',
    [-2]: 'Links abbiegen',
    [-1]: 'Leicht links abbiegen',
    [0]: 'Geradeaus weiterfahren',
    [1]: 'Leicht rechts abbiegen',
    [2]: 'Rechts abbiegen',
    [3]: 'Scharf rechts abbiegen',
    [4]: 'Ziel erreicht',
    [5]: 'Zwischenziel erreicht',
    [6]: 'In den Kreisverkehr einfahren',
    [7]: 'Rechts halten'
  },
  ES: {
    [-7]: 'Mantente a la izquierda',
    [-3]: 'Gira pronunciadamente a la izquierda',
    [-2]: 'Gira a la izquierda',
    [-1]: 'Gira ligeramente a la izquierda',
    [0]: 'Continúa recto',
    [1]: 'Gira ligeramente a la derecha',
    [2]: 'Gira a la derecha',
    [3]: 'Gira pronunciadamente a la derecha',
    [4]: 'Has llegado',
    [5]: 'Punto intermedio alcanzado',
    [6]: 'Entra en la rotonda',
    [7]: 'Mantente a la derecha'
  },
  IT: {
    [-7]: 'Mantieni la sinistra',
    [-3]: 'Svolta tutto a sinistra',
    [-2]: 'Svolta a sinistra',
    [-1]: 'Svolta leggermente a sinistra',
    [0]: 'Prosegui dritto',
    [1]: 'Svolta leggermente a destra',
    [2]: 'Svolta a destra',
    [3]: 'Svolta tutto a destra',
    [4]: 'Sei arrivato',
    [5]: 'Punto intermedio raggiunto',
    [6]: 'Entra nella rotonda',
    [7]: 'Mantieni la destra'
  },
  FR: {
    [-7]: 'Restez à gauche',
    [-3]: 'Tournez franchement à gauche',
    [-2]: 'Tournez à gauche',
    [-1]: 'Tournez légèrement à gauche',
    [0]: 'Continuez tout droit',
    [1]: 'Tournez légèrement à droite',
    [2]: 'Tournez à droite',
    [3]: 'Tournez franchement à droite',
    [4]: 'Vous êtes arrivé',
    [5]: 'Étape intermédiaire atteinte',
    [6]: 'Entrez dans le rond-point',
    [7]: 'Restez à droite'
  }
};

const streetJoiner: Record<LanguageCode, string> = {
  EN: 'onto',
  DE: 'auf',
  ES: 'hacia',
  IT: 'su',
  FR: 'sur'
};

const unavailable: Record<LanguageCode, string> = {
  EN: 'Maneuver unavailable',
  DE: 'Fahrhinweis nicht verfügbar',
  ES: 'Maniobra no disponible',
  IT: 'Manovra non disponibile',
  FR: 'Manœuvre indisponible'
};

const roundaboutExit = (language: LanguageCode, exit: number, street: string): string => {
  const destination = street ? ` ${streetJoiner[language]} ${street}` : '';
  if (language === 'DE') return `Im Kreisverkehr die ${exit}. Ausfahrt${destination} nehmen`;
  if (language === 'ES') return `En la rotonda, toma la salida ${exit}${destination}`;
  if (language === 'IT') return `Alla rotonda, prendi l'uscita ${exit}${destination}`;
  if (language === 'FR') return `Au rond-point, prenez la sortie ${exit}${destination}`;
  return `At the roundabout, take exit ${exit}${destination}`;
};

export function formatInstruction(
  instruction: RouteInstruction,
  language: LanguageCode
): string {
  const providerFallback = instruction.text?.trim();
  const action = actions[language][instruction.sign];
  if (!action) return providerFallback || unavailable[language];

  const street = instruction.street_name.trim();
  if (instruction.sign === 6 && instruction.exited && Number.isInteger(instruction.exit_number)) {
    return roundaboutExit(language, instruction.exit_number as number, street);
  }

  if (!street || instruction.sign === 4 || instruction.sign === 5 || instruction.sign === 6) {
    return action;
  }
  return `${action} ${streetJoiner[language]} ${street}`;
}
